import { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';

import { Box, Link, TypographyProps } from '@mui/material';

import { TRANSACTION_STATUS_INTL_MAP } from '@arrived/common';
import { CONFIG } from '@arrived/config';
import { LEASE_STATUS_MESSAGES_STRINGS, LeaseStatus, TransactionStatus } from '@arrived/models';

import { LeaseStatusMessage } from '../PropertyPage';
import { ArrivedTooltip, TooltipProps } from './ArrivedTooltip';

export interface ArrivedTooltipProps extends Partial<TooltipProps> {
  labelClassName?: string;
  titleVariant?: TypographyProps['variant'];
}

const buildLabel = (props: ArrivedTooltipProps, label: NonNullable<ReactNode>): NonNullable<ReactNode> => {
  const { labelClassName } = props;

  if (labelClassName) {
    return <span className={labelClassName}>{label}</span>;
  }

  return label;
};

export const AccreditedInvestorLearnMoreTooltip = (props: ArrivedTooltipProps) => {
  return (
    <ArrivedTooltip
      {...props}
      title={buildLabel(
        props,
        <FormattedMessage id="accredited-investor-more-info-tooltip-label" defaultMessage="Learn More" />
      )}
    >
      <FormattedMessage
        id="accredited-investor-more-info-tooltip-text"
        defaultMessage="The below questions will help us determine whether you qualify as an 'accredited investor' or whether you are an affiliated person with FINRA or any publicly traded company. Accredited investors may be subject to different investment limits than non-accredited investors."
      />
    </ArrivedTooltip>
  );
};

export const AmlTooltip = (props: ArrivedTooltipProps) => (
  <ArrivedTooltip
    {...props}
    title={buildLabel(props, <FormattedMessage id="aml-tooltip-label" defaultMessage="Anti Money Laundering" />)}
  >
    <FormattedMessage
      id="aml-tooltip-text"
      defaultMessage={`Anti-money laundering (AML) refers to the laws, regulations and procedures intended to prevent criminals from
    disguising illegally obtained funds as legitimate income. Though anti-money laundering laws cover a limited range
    of transactions and criminal behavior, their implications are far-reaching. For example, AML regulations require
    banks and other financial institutions that issue credit or accept customer deposits to follow rules that ensure
    they are not aiding money-laundering.`}
    />
  </ArrivedTooltip>
);

export const InvestorsTooltip = (props: ArrivedTooltipProps) => (
  <ArrivedTooltip {...props} title={<FormattedMessage id="investors-tooltip-label" defaultMessage="Investors" />}>
    <FormattedMessage
      id="investors-tooltip-text"
      defaultMessage="The number of people investing together in this specific property."
    />
  </ArrivedTooltip>
);

export const KycTooltip = (props: ArrivedTooltipProps) => (
  <ArrivedTooltip
    {...props}
    title={buildLabel(props, <FormattedMessage id="kyc-tooltip-label" defaultMessage="Know Your Customer" />)}
  >
    <FormattedMessage
      id="kyc-tooltip-text"
      defaultMessage="The Know Your Customer or KYC process is a required standard that financial services verify their clients are
    genuinely who they claim to be and assess their suitability.
    {br}{br}
    The KYC check is the mandatory process of identifying and verifying the identity of a client when opening an
    account, assessing their suitability, and risks involved with creating a business relationship. These processes
    are part of broader anti-money laundering compliance (AML) and help prevent and identify money laundering,
    terrorism financing, and other illegal corruption schemes."
      values={{ br: <br /> }}
    />
  </ArrivedTooltip>
);

export const MonthlyRentTooltip = (props: ArrivedTooltipProps) => (
  <ArrivedTooltip {...props} title={<FormattedMessage id="monthly-rent-tooltip-label" defaultMessage="Monthly Rent" />}>
    <FormattedMessage id="monthly-rent-tooltip-text" defaultMessage="The gross monthly rent for the property." />
  </ArrivedTooltip>
);

export const PurchasePriceTooltip = (props: ArrivedTooltipProps) => (
  <ArrivedTooltip
    {...props}
    title={<FormattedMessage id="purchase-price-tooltip-label" defaultMessage="Purchase Price" />}
  >
    <FormattedMessage
      id="purchase-price-tooltip-text"
      defaultMessage="The price paid to acquire the property. This does not include the offering fees or cash reserve."
    />
  </ArrivedTooltip>
);

export const LeaseStatusMessageWithTooltip = (props: { leaseStatus: LeaseStatus }) => {
  const { leaseStatus } = props;

  const tooltipStrings = LEASE_STATUS_MESSAGES_STRINGS[leaseStatus].tooltip;
  if (!tooltipStrings) {
    return <LeaseStatusMessage leaseStatus={leaseStatus} />;
  }

  return (
    <ArrivedTooltip {...props} title={<LeaseStatusMessage leaseStatus={leaseStatus} />}>
      <FormattedMessage id={tooltipStrings.id} defaultMessage={tooltipStrings.text} />
    </ArrivedTooltip>
  );
};

export const TaxIdUsageMoreInfoTooltip = () => {
  return (
    <ArrivedTooltip
      title={buildLabel({}, <FormattedMessage id="tax-id-usage-more-info-tooltip-label" defaultMessage="Learn More" />)}
    >
      <FormattedMessage
        id="tax-id-more-info-tooltip-text"
        defaultMessage="We use your tax IDs to verify your identity and for creating your tax documents. We do not run credit checks using your information."
      />
    </ArrivedTooltip>
  );
};

interface TradeStatusTooltipProps extends ArrivedTooltipProps {
  status: TransactionStatus;
}

export const TradeStatusTooltip = (props: TradeStatusTooltipProps) => {
  const { status } = props;
  const transactionStatusList = Object.values(TransactionStatus);

  return (
    <ArrivedTooltip {...props} title={buildLabel(props, TRANSACTION_STATUS_INTL_MAP[status].label)}>
      <>
        <Box
          sx={{
            width: '100%',
            height: 40,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            bgcolor: ({ palette }) => palette.primary.main,
            color: ({ palette }) => palette.neutrals.white,
            fontSize: 20,
            paddingTop: '4px',
          }}
        >
          <FormattedMessage
            id="trade-status-definitions-tooltip-text-title"
            defaultMessage="TRADE STATUS DEFINITIONS"
          />
        </Box>
        <div className="trade-status-table">
          {transactionStatusList.map((transactionStatus, index) => {
            if (transactionStatus !== TransactionStatus.CREATED_PENDING) {
              const { description, label } = TRANSACTION_STATUS_INTL_MAP[transactionStatus];

              return (
                <div key={index} className="key-value-row">
                  <div className="key">{label}</div>
                  <div className="value">{description}</div>
                </div>
              );
            }

            return null;
          })}
        </div>
      </>
    </ArrivedTooltip>
  );
};

export const FlexFinancingTooltip = () => {
  return (
    <ArrivedTooltip title={<FormattedMessage id="common.flexible.future.financing" />} titleVariant="body2">
      <FormattedMessage
        id="flexible.future.financing.description"
        values={{
          a: (parts: any) => (
            <Link
              href={`${CONFIG.helpUrl}articles/6843245-will-arrived-refinance-properties-in-the-future`}
              fontWeight="semibold"
            >
              {parts}
            </Link>
          ),
        }}
      />
    </ArrivedTooltip>
  );
};
